import { mergePreview, rejectProposal, type MergePreviewResponse } from '@/api/proposals'
import { isStageBody, normaliseForSave, useEditorStore, type EditorDoc, type KindDomain, type ProposalView } from '@/stores/editorStore'
import type { StageBody } from '@/schema'

import { diffBodies, type ClientDiff, type FeatureCollection } from './diff'

/**
 * Taking suggestions into the picture — **locally** (explicit-Save model).
 *
 * "Accept" never writes to the server. It asks the server for a dry-run merge
 * (`POST /api/proposals/{id}/merge-preview`, the exact rules `merge.py` uses: locked features and
 * deletions are respected, a matched feature keeps the document's id) of the document ON SCREEN,
 * and puts the answer in as ONE undoable edit. The picture is then "Unsaved"; the next Save sends
 * the document and reports which suggestions it took (`resolved_proposals`) in the same write.
 * Undo takes the suggestions back out.
 *
 * Why the server and not a TypeScript copy of `merge.py`: one set of rules, no drift near the IoU
 * thresholds, and the answer comes back cleaned and linted like a save.
 */

export interface ProposalItem {
  /** Proposal feature id — what `feature_ids` and `accepted_ids` name. */
  id: string
  collection: FeatureCollection
  domain: KindDomain
  kind: string
  label?: string
  confidence: number
  /** `new`: not on the picture yet; `changed`: would move / reshape / re-kind one that is. */
  what: 'new' | 'changed'
  /** The document feature a `changed` item would replace. */
  docId?: string
}

export interface ReviewState {
  /** Still to decide, most confident first. */
  items: ProposalItem[]
  /** Dropped by the reviewer (can be brought back). */
  dropped: ProposalItem[]
  /** Already on the picture (taken, or it matched what was there). */
  settled: number
  /** Left alone because a person locked what is there. */
  lockedSkipped: number
  diff: ClientDiff | null
}

const DOMAIN: Record<FeatureCollection, KindDomain> = { region: 'region_kind', anchor: 'anchor_kind', portal: 'portal_kind', path: 'path_kind' }

function featureInfo(body: StageBody, id: string): { collection: FeatureCollection; kind: string; label?: string; confidence: number } | null {
  const r = body.regions.find((x) => x.id === id)
  if (r) return { collection: 'region', kind: r.kind, label: r.label, confidence: r.provenance.confidence }
  const a = body.anchors.find((x) => x.id === id)
  if (a) return { collection: 'anchor', kind: a.kind, label: a.label, confidence: a.provenance.confidence }
  const p = body.portals.find((x) => x.id === id)
  if (p) return { collection: 'portal', kind: p.kind, confidence: p.provenance.confidence }
  const w = body.paths.find((x) => x.id === id)
  if (w) return { collection: 'path', kind: w.kind, label: w.label, confidence: w.provenance.confidence }
  return null
}

/**
 * What is left to review of `proposal` against the document on screen.
 *
 * `accepted` are the proposal items the document already took (the store's `proposalAccepted`
 * for this proposal — part of every undo step). They are settled whatever the geometry says: the
 * server took them, and a merge that kept the picture as it was (a locked shape, a duplicate
 * folded into one already there) must not leave an item that comes back after every "Keep".
 * Undoing the accept brings them back.
 */
export function reviewStateOf(doc: EditorDoc | null, proposal: ProposalView | null, dropped: readonly string[] = [], accepted: readonly string[] = []): ReviewState {
  const empty: ReviewState = { items: [], dropped: [], settled: 0, lockedSkipped: 0, diff: null }
  if (!proposal || !isStageBody(doc) || !isStageBody(proposal.body)) return empty
  const body = proposal.body
  const diff = diffBodies(doc, body)
  const gone = new Set(dropped)
  const taken = new Set(accepted)
  const docIdOf = new Map(diff.pairs.map((p) => [p.proposed, p.current]))
  const items: ProposalItem[] = []
  const droppedItems: ProposalItem[] = []
  let takenOpen = 0
  const push = (id: string, what: ProposalItem['what']) => {
    const info = featureInfo(body, id)
    if (!info) return
    if (taken.has(id)) {
      takenOpen += 1
      return
    }
    const item: ProposalItem = { id, what, domain: DOMAIN[info.collection], ...info, ...(what === 'changed' ? { docId: docIdOf.get(id) } : {}) }
    if (gone.has(id)) droppedItems.push(item)
    else items.push(item)
  }
  for (const id of diff.added) push(id, 'new')
  for (const c of diff.changed) push(c.id, 'changed')
  items.sort((a, b) => b.confidence - a.confidence)
  return { items, dropped: droppedItems, settled: diff.unchanged.length + takenOpen, lockedSkipped: diff.locked_skipped.length, diff }
}

/** Items a "sure ones" click takes: confidence at or above the threshold. */
export function sureItems(items: readonly ProposalItem[], threshold: number): ProposalItem[] {
  return items.filter((i) => i.confidence >= threshold)
}

export interface AcceptOutcome {
  ok: boolean
  /** Proposal items the merge took. */
  taken: number
  error?: string
}

/**
 * Take `ids` (proposal feature ids) into the document on screen, as one undoable edit. Nothing is
 * written on the server. If the picture changes while the merge is being worked out, the merge is
 * redone once on the newer picture.
 */
export async function acceptProposalItems(ids: readonly string[]): Promise<AcceptOutcome> {
  const st = useEditorStore.getState()
  const proposal = st.proposal
  if (!proposal || !st.doc || ids.length === 0) return { ok: false, taken: 0 }
  let res: MergePreviewResponse | null = null
  let base: EditorDoc | null = null
  for (let attempt = 0; attempt < 2; attempt++) {
    base = useEditorStore.getState().doc
    if (!base) return { ok: false, taken: 0 }
    try {
      res = await mergePreview(proposal.id, { base_body: normaliseForSave(base), feature_ids: [...ids] })
    } catch (err) {
      const e = err as { status?: number; message?: string }
      return {
        ok: false,
        taken: 0,
        error:
          e?.status === 422
            ? 'These suggestions do not fit this picture any more, so they were not added.'
            : e?.status === 404
              ? 'These suggestions are gone (the picture was marked again since). Close them and run the AI again.'
              : (e?.message ?? 'The suggestions could not be added.'),
      }
    }
    const now = useEditorStore.getState()
    if (now.doc === base && !now.gesturing) break
    res = null
  }
  if (!res || !base) return { ok: false, taken: 0, error: 'The picture changed while the suggestions were being added. Try again.' }
  const still = useEditorStore.getState()
  if (still.proposal?.id !== proposal.id) return { ok: false, taken: 0 }
  still.applyProposalMerge(res.body, proposal.id, res.accepted_ids)
  return { ok: true, taken: res.accepted_ids.length }
}

/**
 * "Reject all". Nothing taken yet: tell the server now (it does not touch the document) and close.
 * Some taken: drop the rest locally and close — the next Save reports the proposal as partly used.
 */
export async function rejectRemaining(itemIds: readonly string[]): Promise<{ ok: boolean; error?: string }> {
  const st = useEditorStore.getState()
  const proposal = st.proposal
  if (!proposal) return { ok: true }
  const taken = st.proposalAccepted[String(proposal.id)] ?? []
  if (taken.length === 0) {
    try {
      await rejectProposal(proposal.id)
    } catch (err) {
      return { ok: false, error: (err as Error)?.message ?? 'Could not dismiss the suggestions.' }
    }
  } else if (itemIds.length) {
    st.dropProposalItems(itemIds)
  }
  useEditorStore.getState().setProposal(null)
  return { ok: true }
}
